/**
 * FocusHeaderChip — compact identity + notifications + sign-out for the
 * DashboardFocusShell mode bar.
 *
 * Iter 161 · When the shell is in Focus mode the wrapped role dashboard
 * (which usually renders the site header with logout, notifications,
 * and avatar) is display:none — see the always-mount-children fix in
 * iter 158.2. That left focus-mode users with no visible way to sign
 * out or open notifications. This chip restores those affordances
 * without pulling the entire legacy header back into focus mode.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, LogOut, User as UserIcon, Settings, KeyRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (u) => {
  if (!u) return '—';
  const first = (u.first_name || u.email || '').trim().charAt(0).toUpperCase();
  const last = (u.last_name || '').trim().charAt(0).toUpperCase();
  return `${first}${last}` || (u.email || '?').charAt(0).toUpperCase();
};

const roleLabel = (r) => ({
  admin: 'Admin',
  clinician: 'Clinician',
  agency: 'Agency',
  agency_admin: 'Agency',
  discharge_planner: 'Planner',
  hospital_admin: 'Planner',
  patient: 'Patient',
  family: 'Family',
  caregiver: 'Caregiver',
  payer: 'Payer',
}[r] || (r || 'User'));

const settingsHrefFor = (role) => ({
  clinician: '/clinician/practice-profile',
  agency: '/agency/settings',
  admin: '/admin/dashboard?tab=settings',
  discharge_planner: '/planner/settings',
  hospital_admin: '/planner/settings',
  patient: '/patient/settings',
  payer: '/payer/dashboard',
}[role] || '/privacy/dashboard');

export function FocusHeaderChip({ role }) {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const rootRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    if (openMenu) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenu]);

  // Poll notifications count (light-weight; endpoint is idempotent).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications/unread-count`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setUnread(Number(data?.count) || 0);
      } catch {
        // Endpoint might not exist for every role — treat as zero.
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  if (!user) return null;

  const onSignOut = async () => {
    setOpenMenu(false);
    await logout();
    navigate('/auth', { replace: true });
  };

  return (
    <div ref={rootRef} className="relative inline-flex items-center gap-2" data-testid="focus-header-chip">
      <Link
        to="/notifications"
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-600"
        data-testid="focus-header-notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="focus-header-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Link>

      <button
        type="button"
        onClick={() => setOpenMenu((v) => !v)}
        className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 transition-colors"
        aria-haspopup="menu"
        aria-expanded={openMenu}
        data-testid="focus-header-avatar"
      >
        {user.photo_url ? (
          <img
            src={user.photo_url}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <span className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
            {initialsOf(user)}
          </span>
        )}
        <span className="hidden sm:flex flex-col text-left leading-tight">
          <span className="text-xs font-medium text-slate-900 max-w-[110px] truncate">
            {user.first_name || user.email}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {roleLabel(role || user.role)}
          </span>
        </span>
      </button>

      {openMenu && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-56 rounded-xl border border-slate-200 bg-white shadow-lg py-1.5 z-50"
          data-testid="focus-header-menu"
        >
          <div className="px-3 pt-2 pb-1.5 border-b border-slate-100">
            <div className="text-xs font-medium text-slate-900 truncate">{user.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
              {roleLabel(role || user.role)}
            </div>
          </div>

          <Link
            to={settingsHrefFor(role || user.role)}
            onClick={() => setOpenMenu(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="focus-header-settings"
            role="menuitem"
          >
            <Settings className="w-3.5 h-3.5" /> Settings
          </Link>

          <Link
            to="/privacy/dashboard?panel=mfa"
            onClick={() => setOpenMenu(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="focus-header-mfa"
            role="menuitem"
          >
            <KeyRound className="w-3.5 h-3.5" /> Account security
          </Link>

          <Link
            to="/privacy/dashboard"
            onClick={() => setOpenMenu(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="focus-header-profile"
            role="menuitem"
          >
            <UserIcon className="w-3.5 h-3.5" /> Privacy & data
          </Link>

          <div className="my-1 border-t border-slate-100" />

          <button
            type="button"
            onClick={onSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-700 hover:bg-rose-50"
            data-testid="focus-header-signout"
            role="menuitem"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default FocusHeaderChip;
